import { alpha } from "@mui/material/styles";

const CREDENTIAL_VARIANTS = {
  default: {
    header: ["#667eea", "#764ba2"],
    cta: ["#2196F3", "#21CBF3"],
    ctaHover: ["#1976D2", "#1CB5E0"],
  },
  amazon: {
    outer: ["#fff3e0", "#ffecb3"],
    header: ["#ff9800", "#f57c00"],
    cta: ["#ff9800", "#ff5722"],
  },
};

export function getCredentialPanelStyles(theme, variant = "default") {
  const isDark = theme.palette.mode === "dark";
  const paper = theme.palette.background.paper;
  const v = CREDENTIAL_VARIANTS[variant] || CREDENTIAL_VARIANTS.default;

  return {
    outerCard: {
      border: "3px solid",
      borderColor: "primary.main",
      borderRadius: 3,
      boxShadow: 3,
      background: isDark
        ? `linear-gradient(135deg, ${paper} 0%, ${alpha(theme.palette.primary.main, 0.14)} 100%)`
        : v.outer
          ? `linear-gradient(135deg, ${v.outer[0]} 0%, ${v.outer[1]} 100%)`
          : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    },
    header: {
      background: isDark
        ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.75)} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`
        : `linear-gradient(90deg, ${v.header[0]} 0%, ${v.header[1]} 100%)`,
      p: 4,
      borderRadius: 2,
      color: "#fff",
    },
    headerButton: {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      color: "white",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.3)",
        transform: "translateY(-2px)",
      },
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
    },
    infoAlert: {
      mb: 4,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "info.main",
      background: isDark
        ? alpha(theme.palette.info.main, 0.12)
        : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      color: theme.palette.text.primary,
      "& .MuiAlert-message": { color: theme.palette.text.primary },
      "& a": {
        color: isDark ? theme.palette.info.light : theme.palette.info.main,
      },
    },
    emptyState: {
      textAlign: "center",
      py: 8,
      background: isDark
        ? alpha(theme.palette.common.white, 0.04)
        : "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
      borderRadius: 2,
      border: "2px dashed",
      borderColor: isDark ? alpha(theme.palette.common.white, 0.2) : "grey.400",
    },
    ctaButton: {
      borderRadius: 3,
      px: 4,
      py: 1.5,
      background: isDark
        ? `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`
        : `linear-gradient(45deg, ${v.cta[0]} 30%, ${v.cta[1]} 90%)`,
      boxShadow: isDark
        ? `0 3px 5px 2px ${alpha(theme.palette.primary.main, 0.35)}`
        : "0 3px 5px 2px rgba(33, 203, 243, .3)",
      "&:hover": {
        background: isDark
          ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
          : `linear-gradient(45deg, ${v.ctaHover[0]} 30%, ${v.ctaHover[1]} 90%)`,
      },
    },
    dialogPaper: {
      background: isDark
        ? `linear-gradient(135deg, ${paper} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    },
    dialogHeader: {
      background: isDark
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.55)} 0%, ${alpha(theme.palette.primary.dark, 0.7)} 100%)`
        : `linear-gradient(135deg, ${v.header[0]} 0%, ${v.header[1]} 100%)`,
      color: "#fff",
    },
    warningBanner: {
      background: isDark
        ? alpha(theme.palette.warning.main, 0.15)
        : "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)",
      color: theme.palette.text.primary,
    },
  };
}

export function getCronBuilderSx(theme) {
  const isDark = theme.palette.mode === "dark";

  return {
    width: "100%",
    overflowX: "auto",
    "& .cron_builder": {
      width: "100%",
      minWidth: { xs: "100%", md: "950px" },
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
      backgroundColor: isDark ? theme.palette.background.paper : "#fff",
      color: theme.palette.text.primary,
    },
    "& .cron_builder select, & .cron_builder input, & .cron_builder textarea": {
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.06)
        : "#fff",
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    "& .cron_builder label, & .cron_builder .cron-builder-label, & .cron_builder span":
      {
        color: theme.palette.text.primary,
      },
    "& .nav-tabs .nav-link": {
      color: theme.palette.text.secondary,
    },
    "& .nav-tabs .nav-link.active": {
      backgroundColor: isDark
        ? alpha(theme.palette.primary.main, 0.18)
        : "#fff",
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    "& .cron_builder_bordering, & .well": {
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.04)
        : undefined,
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    },
  };
}

export function getCronStatusPanelSx(theme, isActive) {
  const isDark = theme.palette.mode === "dark";

  if (isDark) {
    return {
      mt: 1,
      p: 3,
      borderRadius: 2,
      background: isActive
        ? alpha(theme.palette.success.main, 0.12)
        : alpha(theme.palette.error.main, 0.12),
      border: "1px solid",
      borderColor: isActive
        ? alpha(theme.palette.success.main, 0.35)
        : alpha(theme.palette.error.main, 0.35),
      color: theme.palette.text.primary,
    };
  }

  return {
    mt: 1,
    p: 3,
    borderRadius: 2,
    background: isActive
      ? "linear-gradient(to right, #edf7ed, #ffffff)"
      : "linear-gradient(to right, #fdeded, #ffffff)",
    border: "1px solid",
    borderColor: isActive ? "#c3e6cb" : "#f5c6cb",
  };
}

export function getMutedPanelSx(theme) {
  const isDark = theme.palette.mode === "dark";

  return {
    mt: 3,
    p: 2,
    bgcolor: isDark ? theme.palette.grey[50] : "#f5f5f5",
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
    color: theme.palette.text.primary,
  };
}

/** Table header row / filter panel surfaces — use grey tokens (dark-aware via theme palette). */
export function getTableHeadRowSx(theme) {
  return {
    backgroundColor: theme.palette.grey[100],
    "& .MuiTableCell-head": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  };
}

export function getFilterPanelSx(theme) {
  return {
    p: 2,
    mb: 2,
    bgcolor: theme.palette.grey[50],
    borderColor: "divider",
    color: theme.palette.text.primary,
  };
}

export function getDetailPanelSx(theme) {
  return {
    px: 2,
    py: 1.5,
    bgcolor: theme.palette.grey[50],
    color: theme.palette.text.primary,
  };
}

/** Subtle error tint for ignored FBM/FBA rows (dark-mode safe). */
export function getIgnoredRowSx(theme, isIgnored) {
  if (!isIgnored) return {};
  return {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.error.main, 0.18)
        : alpha(theme.palette.error.main, 0.1),
  };
}
